import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IndicatorMap } from './models/indicator-map';
import { IndicatorValue } from './models/indicator-value';
import { Indicator } from './models/indicator';

@Injectable({ providedIn: 'root' })
export class IndicatorsService {

  private readonly queryState: Record<string, any> = {};
  private readonly indicatorsCache = new Map<string, Observable<Indicator[]>>();
  private readonly indicatorValuesCache = new Map<string, Observable<IndicatorValue[]>>();

  readonly valueChanges = new BehaviorSubject<Record<string, any>>({});

  constructor(private readonly httpClient: HttpClient) {
  }

  set(key: string, val: any): void {
    this.queryState[key] = val;
    this.valueChanges.next(this.queryState);
  }

  get(): Record<string, any> {
    return this.queryState;
  }

  getIndicators(language: string): Observable<Indicator[]> {
    const cacheKey = language || 'default';
    const existing = this.indicatorsCache.get(cacheKey);

    if (existing) {
      return existing;
    }

    const request$ = this.httpClient
      .get<IndicatorMap>(`${environment.indicators.api.url}/v1/indicators`, {
        params: { language },
      })
      .pipe(
        timeout(15000),
        retry({ count: 1, delay: 250 }),
        map((response) => this.flattenIndicators(response)),
        catchError((error) => {
          this.indicatorsCache.delete(cacheKey);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.indicatorsCache.set(cacheKey, request$);
    return request$;
  }

  getIndicatorValues(indicatorKey: string): Observable<IndicatorValue[]> {
    const existing = this.indicatorValuesCache.get(indicatorKey);

    if (existing) {
      return existing;
    }

    const request$ = this.httpClient
      .get<IndicatorValue[]>(`${environment.indicators.api.url}/v1/indicators/${indicatorKey}/values`)
      .pipe(
        timeout(15000),
        retry({ count: 1, delay: 250 }),
        map((values) => Array.isArray(values) ? values : []),
        catchError((error) => {
          this.indicatorValuesCache.delete(indicatorKey);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.indicatorValuesCache.set(indicatorKey, request$);
    return request$;
  }

  private flattenIndicators(indicatorMap: IndicatorMap): Indicator[] {
    if (Array.isArray(indicatorMap)) {
      return indicatorMap;
    }

    return Object.values(indicatorMap || {}).flatMap((group) =>
      Object.values(group || {}).flatMap((short) => Object.values(short || {}))
    );
  }
}
